import type { IntakeSpec } from '@shared/types';
import type { ComponentType } from 'react';
import { Cog, Flower2, Building2, Wrench, Archive, Armchair } from 'lucide-react';

export type ShowcasePreset = {
  key: 'gear' | 'vase' | 'architectureModel' | 'bracket' | 'cabinet' | 'diningTable';
  label: string;
  icon: ComponentType<{ className?: string }>;
  intake: IntakeSpec;
};

const submittedAt = '2026-04-25T00:00:00.000Z';

export const SHOWCASE_PRESETS: ShowcasePreset[] = [
  {
    key: 'gear',
    label: '歯車',
    icon: Cog,
    intake: {
      category: 'mechanical',
      subType: 'gear',
      qualityLevel: 'extreme',
      core: {
        intendedUse: 'パラメトリックな平歯車',
        size: { length: 60, width: 60, height: 10 },
        // PLA is the realistic material for an at-home prototype gear of this
        // module/scale; sheet-metal stamping doesn't reproduce involute teeth.
        primaryMaterial: 'printed_pla',
      },
      drawing: {
        scope: 'both',
        sectionCuts: 'auto',
        showHoleTable: true,
        showBOM: false,
        toleranceClass: 'tight',
        jisStrict: true,
      },
      details: {
        category: 'mechanical',
        subType: 'gear',
        gearType: 'spur',
        module: 1.5,
        toothCount: 20,
        pressureAngle: 20,
        faceWidth: 10,
        boreDiameter: 6,
        hubStyle: 'set_screw',
      },
      freeform:
        'Crisp involute teeth (full profile, no truncation), keyed central bore for a 6mm shaft, and a small hub with set-screw recess. Edge chamfer on the face to break sharp edges.',
      submittedAt,
    },
  },
  {
    key: 'vase',
    label: '花瓶',
    icon: Flower2,
    intake: {
      category: 'printing',
      subType: 'decorative',
      qualityLevel: 'standard',
      core: {
        intendedUse: '有機的な形状の花瓶',
        size: { length: 100, width: 100, height: 200 },
        primaryMaterial: 'printed_pla',
      },
      drawing: {
        scope: 'assembly_only',
        sectionCuts: 'none',
        showHoleTable: false,
        showBOM: false,
        toleranceClass: 'loose',
        jisStrict: false,
      },
      details: {
        category: 'printing',
        subType: 'decorative',
        itemType: 'vase',
        printer: 'FDM',
        layerHeight: 0.2,
        // Vase mode prints exactly one perimeter — for a 0.4mm nozzle that's
        // ~0.8–1.2mm wall. 1.2 keeps vase mode active; 1.6 silently disabled it.
        shellThickness: 1.2,
        supportPolicy: 'avoid',
        vaseModeCompatible: true,
      },
      freeform:
        'Smooth rotational profile widening at the shoulder then tapering to a narrow neck. Wavy or fluted exterior pattern that catches light. No top — the form is the highlight.',
      submittedAt,
    },
  },
  {
    key: 'architectureModel',
    label: '建築模型',
    icon: Building2,
    intake: {
      category: 'architecture',
      subType: 'building',
      qualityLevel: 'standard',
      core: {
        intendedUse: 'モダンな住宅の建築模型',
        size: { length: 12000, width: 8000, height: 7000 },
      },
      drawing: {
        scope: 'assembly_only',
        sectionCuts: 'none',
        showHoleTable: false,
        showBOM: false,
        toleranceClass: 'standard',
        jisStrict: false,
      },
      details: {
        category: 'architecture',
        subType: 'building',
        buildingType: 'house',
        scaleRatio: '1:100',
        floorCount: 2,
        roofStyle: 'gable',
        showInterior: false,
        showLandscape: false,
      },
      freeform:
        'Clean modern facade — large picture windows on the south wall, narrower openings elsewhere. Crisp roof eave overhang of about 5mm at scale. A small chimney off-center on the gable.',
      submittedAt,
    },
  },
  {
    key: 'bracket',
    label: 'ブラケット',
    icon: Wrench,
    intake: {
      category: 'mechanical',
      subType: 'bracket',
      qualityLevel: 'extreme',
      core: {
        intendedUse: '取り付け用Lブラケット',
        size: { length: 100, width: 60, height: 4 },
        primaryMaterial: 'sheet_metal',
      },
      drawing: {
        scope: 'both',
        sectionCuts: 'auto',
        showHoleTable: true,
        showBOM: true,
        toleranceClass: 'standard',
        jisStrict: true,
      },
      details: {
        category: 'mechanical',
        subType: 'bracket',
        bracketType: 'L',
        mountingPattern: 'corner',
        holeCount: 4,
        holeSize: 'M5',
        holeFinish: 'countersunk',
        loadDirection: 'shear',
        surfaceFinish: 'powder_coat',
      },
      freeform:
        'Reinforcing gusset between the two flanges to handle the shear load. M5 holes with proper countersink so flat-head screws sit flush. Bend radius of 3mm at the inside corner.',
      submittedAt,
    },
  },
  {
    key: 'cabinet',
    label: '食器棚',
    icon: Archive,
    intake: {
      category: 'furniture',
      subType: 'cabinet',
      qualityLevel: 'extreme',
      core: {
        intendedUse: '2扉の壁付け食器棚',
        // 800 × 350 × 750 mm — typical wall-mount kitchen cabinet footprint.
        size: { length: 800, width: 350, height: 750 },
        // Plywood broadens the catalog showcase (no other preset uses it).
        primaryMaterial: 'plywood',
        fastenerPreference: 'screws',
      },
      drawing: {
        scope: 'both',
        sectionCuts: 'auto',
        showHoleTable: true,
        showBOM: true,
        toleranceClass: 'standard',
        jisStrict: false,
      },
      details: {
        category: 'furniture',
        subType: 'cabinet',
        cabinetType: 'wall',
        doorStyle: 'shaker',
        doorCount: 2,
        drawerCount: 0,
        shelvesInside: 2,
        // Cup hinges showcase the pocket(purpose='hinge_cup') primitive,
        // which is the canonical home for the 35mm Blum/Salice cup.
        hardwareStyle: 'cup_hinge',
        joineryType: 'dado',
        finish: 'lacquer',
      },
      freeform:
        'Two shaker-style doors with concealed cup hinges, 18mm plywood throughout, dado joinery for the shelves, and a 4mm reveal between the doors. Adjustable shelf pin holes drilled along the side panels.',
      submittedAt,
    },
  },
  {
    key: 'diningTable',
    label: 'ダイニングテーブル',
    icon: Armchair,
    intake: {
      category: 'furniture',
      subType: 'table',
      qualityLevel: 'extreme',
      core: {
        intendedUse: '6人用のダイニングテーブル',
        size: { length: 1800, width: 900, height: 720 },
        primaryMaterial: 'hardwood',
        fastenerPreference: 'mortise_tenon',
      },
      drawing: {
        scope: 'both',
        sectionCuts: 'auto',
        showHoleTable: false,
        showBOM: true,
        toleranceClass: 'standard',
        jisStrict: false,
      },
      details: {
        category: 'furniture',
        subType: 'table',
        numSeats: 6,
        shape: 'rectangular',
        legStyle: 'tapered',
        joineryType: 'mortise_tenon',
        finish: 'oil',
        hasStretcher: false,
        hasApron: true,
      },
      freeform:
        'Solid walnut top with a 3mm roundover on every visible edge, four tapered legs (1.0 → 0.6 ratio), and apron joined to legs with through mortise-and-tenon joints. Oiled finish, no metal fasteners visible.',
      submittedAt,
    },
  },
];
